import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {Link} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {useClinic} from '../contexts/ClinicContext';
import axios from 'axios';

const {FiHeart,FiAward,FiUsers,FiClock,FiLogIn,FiLogOut,FiPhone,FiImage,FiBarChart3,FiMapPin,FiMail,FiCalendar,FiShield,FiStar,FiCheckCircle,FiArrowRight}=FiIcons;

const About=()=> {
  const {user,logout}=useAuth();
  const {clinic_name,logo_url,phone,email,address,business_hours,loading: clinicLoading}=useClinic();
  const [doctors,setDoctors]=useState([]);
  const [loadingDoctors,setLoadingDoctors]=useState(true);

  // Static images as fallback for seeded doctors
  const staticDoctorImages={
    'Dr. Ruby': 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=300&h=300&fit=crop&crop=face',
    'Dr. Amna': 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300&h=300&fit=crop&crop=face',
    'Dr. Nisha': 'https://images.unsplash.com/photo-1594824388862-a903b23a0a93?w=300&h=300&fit=crop&crop=face',
    'Dr. Ansad': 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=300&h=300&fit=crop&crop=face'
  };

  useEffect(()=> {
    const fetchDoctors=async ()=> {
      setLoadingDoctors(true);
      try {
        const response=await axios.get('/api/doctors');
        const activeDoctors=response.data
          .filter(d=> d.active)
          .slice(0,4)
          .map(d=> ({
            ...d,
            image: d.profile_image || staticDoctorImages[d.name] || `https://ui-avatars.com/api/?name=${encodeURIComponent(d.name)}&background=random`,
            specialty: d.specialization,
            description: d.bio,
          }));
        setDoctors(activeDoctors);
      } catch (error) {
        console.error("Error fetching doctors for about page:",error);
      }
      setLoadingDoctors(false);
    };
    fetchDoctors();
  },[]);

  // Get clinic name initials for the logo fallback
  const getInitials=(name)=> {
    return name
      .split(' ')
      .map(word=> word[0])
      .join('')
      .substring(0,2)
      .toUpperCase();
  };

  const formatTime=(timeString)=> {
    if (!timeString) return 'Closed';
    const [hour,minute]=timeString.split(':');
    const h=parseInt(hour);
    const ampm=h >=12 ? 'PM' : 'AM';
    const formattedHour=h % 12 || 12;
    return `${formattedHour}:${minute} ${ampm}`;
  };

  const dayOrder=['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

  const features=[
    {
      icon: FiHeart,
      title: 'Patient-Centered Care',
      description: 'We prioritize your comfort and well-being in every treatment with personalized care plans.',
      color: 'from-red-400 to-red-600'
    },
    {
      icon: FiAward,
      title: 'Expert Dentists',
      description: 'Our team consists of highly qualified and experienced dental professionals with international training.',
      color: 'from-blue-400 to-blue-600'
    },
    {
      icon: FiUsers,
      title: 'Family Friendly',
      description: 'Comprehensive dental care for patients of all ages in a welcoming,comfortable environment.',
      color: 'from-green-400 to-green-600'
    },
    {
      icon: FiClock,
      title: 'Flexible Hours',
      description: 'Extended hours including evenings and weekends to fit your busy schedule.',
      color: 'from-purple-400 to-purple-600'
    },
    {
      icon: FiShield,
      title: 'Advanced Technology',
      description: 'State-of-the-art equipment and latest dental technologies for precise,comfortable treatment.',
      color: 'from-indigo-400 to-indigo-600'
    },
    {
      icon: FiStar,
      title: 'Quality Assurance',
      description: 'Commitment to the highest standards of dental care with proven results and patient satisfaction.',
      color: 'from-yellow-400 to-yellow-600'
    }
  ];

  const services=[
    {
      title: 'General Dentistry',
      description: 'Comprehensive oral health care including cleanings,fillings,and preventive treatments.',
      treatments: ['Regular Cleanings','Cavity Fillings','Root Canal Therapy','Tooth Extractions']
    },
    {
      title: 'Cosmetic Dentistry',
      description: 'Enhance your smile with our aesthetic dental procedures and treatments.',
      treatments: ['Teeth Whitening','Porcelain Veneers','Smile Makeovers','Composite Bonding']
    },
    {
      title: 'Orthodontics',
      description: 'Straighten teeth and correct bite issues with modern orthodontic solutions.',
      treatments: ['Traditional Braces','Clear Aligners','Invisalign','Retainers']
    },
    {
      title: 'Oral Surgery',
      description: 'Advanced surgical procedures for complex dental conditions and implants.',
      treatments: ['Dental Implants','Wisdom Teeth Removal','Bone Grafting','Surgical Extractions']
    }
  ];

  const stats=[
    {number: '10+',label: 'Years of Excellence'},
    {number: '5000+',label: 'Happy Patients'},
    {number: '4',label: 'Expert Doctors'},
    {number: '24/7',label: 'Emergency Care'}
  ];

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-maroon-600 via-maroon-700 to-maroon-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        {/* Navigation */}
        <div className="relative z-20">
          <div className="absolute top-4 right-4">
            <div className="flex items-center space-x-2">
              {/* Dashboard button - only show if user is logged in */}
              {user && (
                <Link
                  to="/dashboard"
                  className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-full hover:bg-opacity-30 transition-colors flex items-center space-x-2 border border-white border-opacity-30"
                >
                  <SafeIcon icon={FiBarChart3} className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>
              )}
              {/* Contact Us tab */}
              <Link
                to="/contact"
                className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-full hover:bg-opacity-30 transition-colors flex items-center space-x-2 border border-white border-opacity-30"
              >
                <SafeIcon icon={FiPhone} className="w-4 h-4" />
                <span>Contact</span>
              </Link>
              {/* Gallery tab */}
              <Link
                to="/gallery"
                className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-full hover:bg-opacity-30 transition-colors flex items-center space-x-2 border border-white border-opacity-30"
              >
                <SafeIcon icon={FiImage} className="w-4 h-4" />
                <span>Gallery</span>
              </Link>
              {/* Login/Logout button */}
              {user ? (
                <button
                  onClick={logout}
                  className="bg-white text-maroon-600 px-4 py-2 rounded-full hover:bg-grey-100 transition-colors flex items-center space-x-2"
                >
                  <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              ) : (
                <Link
                  to="/login"
                  className="bg-white text-maroon-600 px-4 py-2 rounded-full hover:bg-grey-100 transition-colors flex items-center space-x-2"
                >
                  <SafeIcon icon={FiLogIn} className="w-4 h-4" />
                  <span>Login</span>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0,x: -50}}
              animate={{opacity: 1,x: 0}}
              transition={{duration: 0.8}}
            >
              {/* Logo and Clinic Name */}
              <div className="flex items-center mb-8">
                {logo_url ? (
                  <div className="w-16 h-16 overflow-hidden rounded-full border-4 border-white shadow-lg mr-4">
                    <img src={logo_url} alt={clinic_name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center border-4 border-white shadow-lg mr-4">
                    <span className="text-maroon-600 font-bold text-2xl">{getInitials(clinic_name)}</span>
                  </div>
                )}
                <h1 className="text-3xl lg:text-4xl font-bold">{clinic_name}</h1>
              </div>

              <h2 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                Your Smile is Our
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">
                  Priority
                </span>
              </h2>
              
              <p className="text-xl lg:text-2xl text-maroon-100 mb-8 leading-relaxed">
                Providing exceptional dental care in Old Al Ghanim, Doha with cutting-edge technology 
                and a commitment to your comfort and well-being.
              </p>

              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/contact"
                  className="bg-white text-maroon-600 px-8 py-4 rounded-full font-semibold hover:bg-grey-100 transition-colors flex items-center justify-center space-x-2 shadow-lg"
                >
                  <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                  <span>Book Appointment</span>
                </Link>
                <Link
                  to="/gallery"
                  className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-maroon-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  <span>Learn More</span>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{opacity: 0,x: 50}}
              animate={{opacity: 1,x: 0}}
              transition={{duration: 0.8,delay: 0.2}}
              className="relative"
            >
              <div className="relative z-10">
                <img
                  src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600&h=500&fit=crop"
                  alt="Modern dental office"
                  className="rounded-2xl shadow-2xl"
                />
                {/* Floating Stats Card */}
                <div className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiStar} className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-grey-900">5000+</div>
                      <div className="text-sm text-grey-600">Happy Patients</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat,index)=> (
              <motion.div
                key={stat.label}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{delay: index * 0.1}}
                className="text-center"
              >
                <div className="text-4xl lg:text-5xl font-bold text-maroon-600 mb-2">{stat.number}</div>
                <div className="text-grey-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-grey-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-grey-900 mb-4">Why Choose EasyAI?</h2>
            <p className="text-xl text-grey-600 max-w-3xl mx-auto">
              We're committed to providing exceptional dental care with a personal touch, 
              using the latest technology and techniques.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature,index)=> (
              <motion.div
                key={feature.title}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{delay: 0.2 + index * 0.1}}
                className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-grey-900 mb-4 text-center">{feature.title}</h3>
                <p className="text-grey-600 text-center leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-grey-900 mb-4">Our Services</h2>
            <p className="text-xl text-grey-600 max-w-3xl mx-auto">
              Comprehensive dental care services designed to meet all your oral health needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service,index)=> (
              <motion.div
                key={service.title}
                initial={{opacity: 0,x: index % 2 === 0 ? -20 : 20}}
                animate={{opacity: 1,x: 0}}
                transition={{delay: 0.3 + index * 0.1}}
                className="bg-gradient-to-br from-grey-50 to-grey-100 rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className="text-2xl font-bold text-grey-900 mb-4">{service.title}</h3>
                <p className="text-grey-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.treatments.map((treatment,idx)=> (
                    <li key={idx} className="flex items-center text-grey-700">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mr-3" />
                      {treatment}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-20 bg-grey-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-grey-900 mb-4">Meet Our Expert Team</h2>
            <p className="text-xl text-grey-600 max-w-3xl mx-auto">
              Our skilled dentists bring years of experience and dedication to your dental care
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {loadingDoctors ? (
              <p>Loading doctors...</p>
            ) : (
              doctors.map((doctor,index)=> (
                <motion.div
                  key={doctor.id}
                  initial={{opacity: 0,y: 20}}
                  animate={{opacity: 1,y: 0}}
                  transition={{delay: 0.4 + index * 0.1}}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="relative">
                    <img
                      src={doctor.image}
                      alt={doctor.name}
                      className="w-full h-64 object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-grey-900 mb-2">{doctor.name}</h3>
                    <p className="text-maroon-600 font-semibold mb-3">{doctor.specialty}</p>
                    <p className="text-grey-600 text-sm">{doctor.description}</p>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-20 bg-maroon-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0,x: -20}}
              animate={{opacity: 1,x: 0}}
              transition={{delay: 0.5}}
            >
              <h2 className="text-4xl font-bold mb-6">Ready to Schedule Your Visit?</h2>
              <p className="text-xl text-maroon-100 mb-8">
                Contact us today to book your appointment and take the first step towards a healthier, brighter smile.
              </p>
              
              <div className="space-y-4">
                {phone && (
                  <div className="flex items-center">
                    <SafeIcon icon={FiPhone} className="w-6 h-6 text-maroon-200 mr-4" />
                    <span className="text-lg">{phone}</span>
                  </div>
                )}
                {email && (
                  <div className="flex items-center">
                    <SafeIcon icon={FiMail} className="w-6 h-6 text-maroon-200 mr-4" />
                    <span className="text-lg">{email}</span>
                  </div>
                )}
                {address && (
                  <div className="flex items-start">
                    <SafeIcon icon={FiMapPin} className="w-6 h-6 text-maroon-200 mr-4 mt-1" />
                    <span className="text-lg">{address}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mt-8">
                <Link
                  to="/contact"
                  className="bg-white text-maroon-600 px-8 py-4 rounded-full font-semibold hover:bg-grey-100 transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                  <span>Contact Us</span>
                </Link>
                <Link
                  to="/gallery"
                  className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-maroon-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiImage} className="w-5 h-5" />
                  <span>View Gallery</span>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{opacity: 0,x: 20}}
              animate={{opacity: 1,x: 0}}
              transition={{delay: 0.6}}
              className="relative"
            >
              <div className="bg-white bg-opacity-10 rounded-2xl p-8 backdrop-blur-sm">
                <h3 className="text-2xl font-bold mb-6">Office Hours</h3>
                <div className="space-y-4">
                  {clinicLoading ? (
                    <p>Loading office hours...</p>
                  ) : business_hours && Object.keys(business_hours).length > 0 ? (
                    dayOrder.map(day => {
                        const hours = business_hours[day];
                        if (!hours) return null;
                        return (
                            <div className="flex justify-between" key={day}>
                                <span className="capitalize">{day}</span>
                                <span>{formatTime(hours.open)} - {formatTime(hours.close)}</span>
                            </div>
                        )
                    })
                  ) : (
                    <p>Office hours not available.</p>
                  )}
                </div>
                
                <div className="mt-8 p-4 bg-red-500 bg-opacity-20 rounded-lg border border-red-300 border-opacity-30">
                  <h4 className="font-semibold mb-2">Emergency Care Available</h4>
                  <p className="text-sm text-maroon-100">
                    For dental emergencies outside office hours, please call our emergency line.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{opacity: 0,x: -20}}
              animate={{opacity: 1,x: 0}}
              transition={{delay: 0.7}}
              className="bg-gradient-to-br from-maroon-50 to-maroon-100 rounded-2xl p-8"
            >
              <div className="w-16 h-16 bg-maroon-600 rounded-2xl flex items-center justify-center mb-6">
                <SafeIcon icon={FiHeart} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-grey-900 mb-6">Our Mission</h3>
              <p className="text-grey-700 text-lg leading-relaxed">
                To provide comprehensive, compassionate dental care using the latest technology and techniques, 
                while maintaining the highest standards of safety and comfort for our patients. We strive to 
                create lasting relationships built on trust, excellence, and genuine care.
              </p>
            </motion.div>

            <motion.div
              initial={{opacity: 0,x: 20}}
              animate={{opacity: 1,x: 0}}
              transition={{delay: 0.8}}
              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8"
            >
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <SafeIcon icon={FiAward} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-grey-900 mb-6">Our Vision</h3>
              <p className="text-grey-700 text-lg leading-relaxed">
                To be the leading dental care provider in Qatar, recognized for our commitment to excellence, 
                innovation, and patient satisfaction. We envision a community where everyone has access to 
                quality dental care and the confidence that comes with a healthy, beautiful smile.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;